/* =========================================================================
 * CLEAR THIS CROWN — the pure model. No React, no imports but the api helper.
 *
 * Brian: correcting a bad crown should be a control in the Command Center. One
 * authenticated call, triggerable from a phone, no terminal, no sandbox,
 * nothing living on his machine.
 *
 * WHAT THE ACTION ACTUALLY IS, and the copy follows from it: this WITHDRAWS AN
 * UNSOURCED CLAIM. It does not delete data. It restores the record to the state
 * it should have been in all along — a championship with no champion
 * established — and it leaves the withdrawal narrated on the record itself
 * (`champion_detected_raw`) plus a ledger row per field. The championship
 * itself — name, dates, state, level, segment — is not touched.
 *
 * ⚠️ THE CLIENT PREDICATE IS A MIRROR, NOT A GATE. `sourceless-crown`'s match()
 * in sr-portal-backend/repair-defects.js is the authorization and it re-decides
 * every one of these clauses against the record IT read. This copy exists so a
 * record that does not qualify is never offered the control, and so the REASON
 * it qualifies can be shown above the button. If the two ever disagree the
 * server wins and the refusal is displayed.
 *
 * ⚠️ PURE, WITH ZERO IMPORTS, AND THAT IS LOAD-BEARING. The network half lives
 * in `clearCrownRun.js`, which is the only thing here that touches
 * `lib/api.js`. `lib/api.js` reads `import.meta.env`, so anything importing it
 * cannot be driven under plain Node — and every rule in this file is a rule a
 * verify suite has to be able to EXECUTE rather than regex for. Same split, and
 * the same reason, as `canvaGap.js` beside `canvaSource.js`.
 * ========================================================================= */

const S = (v) => String(v ?? '').trim()

export const RULE = 'sourceless-crown'

/** The blast-radius cap, mirrored from `repair-defects.js#SOURCELESS_CROWN_CAP`
 *  so the cap refusal can be explained before it is hit. */
export const CLEAR_CAP = 6

/**
 * ⚠️ THE PAGE WALK IS WHY THIS TAKES SEVERAL ROUND TRIPS, and getting it wrong
 * is the silent no-op this whole build exists to avoid.
 *
 * `ids` narrows AFTER the walk, not before it: `repairDefects` pages the whole
 * object and skips every record not in the set. One call walks at most
 * `maxPages` × 100 records — the server clamps maxPages to 25, so 2,500 — and
 * the championship object is ~14,900 records. A record on page 100 is simply
 * not reached, and the call comes back `written: 0, done: false` with a cursor.
 *
 * That response is NOT "it did not qualify" and must never be read as one. So
 * the run resumes from `nextAfter` until the record is written or the walk is
 * genuinely done. Bounded: 8 rounds × 2,500 covers 20,000 records, comfortably
 * past the object, and a loop with no ceiling is a loop that hammers HubSpot
 * when an assumption changes.
 */
export const MAX_ROUNDS = 8
const PAGES_PER_ROUND = 25

/* ---- WHO CAN EVEN BE ASKED ------------------------------------------------
 *
 * A pursuit-adapted row (an RFP, a police department, a corporate account) has
 * no record in HubSpot 2-64425303 at all, so there is nothing to clear. Same
 * test `canHunt` uses.
 */
export function isChampionshipRecord(record) {
  const c = record || {}
  return Boolean(c.id) && !c.sourceType && !c.retired
}

/* ---- DOES THIS RECORD QUALIFY, AND WHY --------------------------------
 *
 * Returns the four clauses of the server predicate, each with the sentence a
 * person reads. `ok` is the conjunction.
 *
 * ⚠️ "No source on file" IS THE JUSTIFICATION FOR THE ACTION, so it is returned
 * as prose rather than a boolean. A button with no reason above it is how a
 * GOOD crown gets cleared by somebody who misread the row — and this control
 * removes a champion from a live record, so that misread is expensive.
 */
export function clearEligibility(record, { readDegraded = false } = {}) {
  const c = record || {}
  const champion = S(c.champion) || S(c.winningSchool)
  const source = S(c.championSourceUrl)
  const locked = Boolean(c.championLocked)
  const retracted = /^CROWN RETRACTED/.test(S(c.championDetectedRaw))

  /* ⚠️ A DEGRADED READ IS NOT A READ — the same refusal the server makes, made
   * here first. If the championship page fell back to CORE_PROPS, both
   * `champion_source_url` and `champion_locked` come back undefined on EVERY
   * record, so every crowned record would read as sourceless and unlocked and
   * this control would appear on all of them. The server refuses such a run
   * outright; drawing the control anyway would be offering an action that
   * cannot run, on records that do not qualify. */
  if (readDegraded) {
    return {
      ok: false,
      blocker: 'degraded',
      reason: 'The championship read fell back to a reduced property set, so the source and lock '
        + 'fields did not come back for any record. Nothing here can be judged until it reloads '
        + 'cleanly — and the server refuses this correction on a degraded read for the same reason.',
      champion, locked,
    }
  }

  if (!champion) {
    return { ok: false, blocker: 'nochampion', reason: 'No champion is recorded on this record.', champion, locked }
  }

  /* ⚠️ LOCKED IS AN ABSOLUTE VETO AND IT IS SHOWN, NEVER HIDDEN. A human set
   * that lock deliberately and it outranks this correction entirely. A rep who
   * cannot see WHY the control is unavailable concludes the feature is broken
   * and asks for it to be fixed. */
  if (locked) {
    return {
      ok: false,
      blocker: 'locked',
      reason: `A person locked the champion on this record, and that decision outranks this correction. `
        + `"${champion}" stays until whoever locked it unlocks it.`,
      champion, locked,
    }
  }

  if (source) {
    return {
      ok: false,
      blocker: 'sourced',
      reason: `This crown cites a source, so it is not an unsupported claim and this correction does not `
        + `apply to it. If "${champion}" is wrong despite the source, correct it through the hunt — `
        + `record the right champion with the right receipt.`,
      champion, locked, source,
    }
  }

  if (retracted) {
    return {
      ok: false,
      blocker: 'retracted',
      reason: 'This crown has already been withdrawn once and the record still carries that account.',
      champion, locked,
    }
  }

  return {
    ok: true,
    blocker: '',
    champion,
    locked: false,
    reason: `This record names "${champion}" as champion and carries no source URL — nothing on the `
      + `record supports the claim. A crown with no source is not a crown.`,
  }
}

/** The control is OFFERED (live or disabled-with-reason) only where the record
 *  is a championship that currently names a champion. A record with no champion
 *  has nothing to withdraw, so it gets no control at all rather than a disabled
 *  one — there is no question being answered. */
export function canClearCrown(record) {
  return isChampionshipRecord(record) && Boolean(S(record?.champion) || S(record?.winningSchool))
}

/* ---- WHAT WILL BE CLEARED, IN WORDS ------------------------------------
 * Mirrors `repair-defects.js#CROWN_DERIVED`. Shown in the confirmation so the
 * gesture is made against the real consequence, not against a vague one.
 */
export const CLEARS = [
  'the champion',
  'its provenance — how and when it was detected, and by what',
  'its story — score, runner-up, season record, venue, coach, roster and title wording',
]

export const KEEPS = 'the championship itself — its name, dates, state, level and segment'

/* ---- READING THE VERDICT -----------------------------------------------
 *
 * ⚠️ THE ROUTE ANSWERS 200 ON EVERY REFUSAL. Cap exceeded, degraded read, an
 * undeclared blank, a failed write — all arrive inside a successful HTTP
 * response with `written: 0`. The measured failure mode in this exact area is a
 * route that returns ok having changed nothing (`champion-update` drops blanks
 * into `ignored`), so NOTHING but `written` is allowed to mean a change
 * happened, and the four zero-write outcomes are told apart from each other
 * rather than collapsed into "failed".
 */
export function readClearResult(res, id) {
  const r = res || {}
  const key = String(id || '')
  const errors = Array.isArray(r.errors) ? r.errors : []
  const sample = (Array.isArray(r.samples) ? r.samples : []).find((s) => String(s?.id) === key) || null

  /* Counted BEFORE any write and refused outright, so nothing landed. Not an
   * error — a finding about the shape of the problem. */
  if (r.capExceeded) {
    return {
      kind: 'cap',
      census: r.census?.[RULE] || null,
      message: errors[0]
        || `More records match this correction than its cap of ${CLEAR_CAP} allows, so it refused to run.`,
      hint: 'Nothing was written. A rule matching this many records is a bug in the rule, not a mass '
        + 'defect for an unattended job to fix — this needs a person before any of them are cleared.',
    }
  }

  if (r.degradedRead) {
    return {
      kind: 'degraded',
      message: errors[0] || 'The championship read came back without the source and lock fields.',
      hint: 'Nothing was written. Reload and try again.',
    }
  }

  /* WRITTEN IS THE ONLY YES. Checked before `errors`, because a write can
   * succeed and its ledger row still fail — that is "cleared, but the audit row
   * did not land", which is a different and worse thing than either a clean
   * success or a refusal, and it must be said out loud. */
  if (Number(r.written) >= 1) {
    return {
      kind: 'cleared',
      fields: sample?.fields || null,
      unlogged: errors.filter((e) => /WROTE but FAILED TO LOG/.test(String(e))),
      message: 'The claim is withdrawn. The record reads as having no champion established, and why '
        + 'it was withdrawn is on the record itself.',
    }
  }

  if (errors.length) {
    return { kind: 'refused', message: errors[0], hint: 'Nothing was written.' }
  }

  /* Not finished walking. NOT a no-op — the record simply has not been reached
   * yet. Reported so the caller resumes rather than declaring an outcome. */
  if (!r.done && r.nextAfter) return { kind: 'more', after: r.nextAfter }

  /* The walk finished and the record never matched. The server read it and
   * disagreed with this client's view of it — which is the server being right
   * by construction. Said plainly rather than dressed as a failure. */
  if (r.done) {
    return {
      kind: 'nomatch',
      message: 'The record was read and it does not qualify — it is not a crown without a source any more.',
      hint: 'Nothing was written. Close and reopen the record to see its current state.',
    }
  }

  return {
    kind: 'unknown',
    message: 'The correction returned without saying whether it reached this record.',
    hint: 'Nothing is confirmed written. Reload the record before trying again.',
  }
}

/* ---- THE OPTIMISTIC PATCH ----------------------------------------------
 *
 * Composed from `samples[].fields` — the fields the response says it wrote —
 * and nothing else, the way `optimisticPatch()` does for the recognition
 * create. `apply()` only emits a field the record actually held, so this is
 * exactly what was destroyed and no more.
 *
 * ⚠️ NORMALIZED KEYS, NOT HUBSPOT ONES. `patchLocal` merges into the normalized
 * record the cells read; patching `current_champion` would leave a stale
 * `champion` on screen beside it. (CommandCenter's `onHunted` has this wrong
 * today — see the note there.)
 *
 * A response that named no fields returns null rather than a guessed patch: the
 * caller reloads instead of inventing a state the server did not confirm.
 */
export function clearPatch(fields) {
  const f = fields || {}
  const has = (k) => Object.prototype.hasOwnProperty.call(f, k)
  const patch = {}

  if (has('current_champion')) {
    patch.champion = ''
    patch.winningSchool = ''
    /* The sentence is composed FROM the story fields, so it cannot survive them.
     * `source: 'none'` is championSummary()'s own no-champion state. */
    patch.summary = { text: '', source: 'none', parts: [] }
  }
  if (has('champion_source_url')) patch.championSourceUrl = ''
  if (has('champion_provenance')) patch.championProvenance = ''
  if (has('champion_entered_by')) patch.championEnteredBy = ''

  const story = {}
  if (has('champion_final_score')) story.score = ''
  if (has('champion_runner_up')) story.runnerUp = ''
  if (has('champion_season_record')) story.record = ''
  if (has('champion_venue')) story.venue = ''
  if (has('champion_head_coach')) story.coach = ''
  if (has('champion_roster_url')) story.rosterUrl = ''
  if (has('champion_title_line')) story.titleLine = ''
  if (has('champion_story')) story.text = ''
  if (has('champion_story_source')) story.sourceUrl = ''
  if (Object.keys(story).length) patch.story = story

  return Object.keys(patch).length ? patch : null
}

/* ---- THE REQUEST ONE ROUND SENDS ---------------------------------------
 *
 * Composed here rather than in the runner so the shape of the call is testable
 * without a network stack. `ids` NARROWS and nothing in this body can widen a
 * predicate — see the note at the top of `lib/api.js#championsRepairDefects`.
 */
export function clearRequest(id, after = null) {
  return {
    only: RULE,
    ids: [String(id)],
    dryRun: false,
    /* One record is the whole job. The limit is a second ceiling under the
     * `ids` narrowing, not the mechanism — belt and braces on a write. */
    limit: 1,
    maxPages: PAGES_PER_ROUND,
    after,
  }
}

/** The champion being removed — the value the record will no longer hold, and
 *  the one worth having in the audit row. After the write nothing carries it
 *  except the retraction narrative and the ledger. */
export function clearBefore(record) {
  return {
    champion: S(record?.champion) || S(record?.winningSchool),
    championSourceUrl: S(record?.championSourceUrl),
    championship: S(record?.name),
  }
}

/** ⚠️ RAN OUT OF ROUNDS IS NOT A FAILURE AND NOT A SUCCESS. Collapsing it into
 *  either one is how a record nobody reached gets reported as cleared. */
export function unfinished() {
  return {
    kind: 'unfinished',
    rounds: MAX_ROUNDS,
    message: `Read ${MAX_ROUNDS * PAGES_PER_ROUND * 100} records without reaching this one.`,
    hint: 'Nothing was written. The object is larger than this correction expects to walk — worth saying '
      + 'so rather than trying again blindly.',
  }
}
